import React, { useContext, useEffect, useState } from 'react';
import { Box, Card, Grid, Typography, TextField, MenuItem, FormControlLabel, Switch, Collapse, IconButton, Button, Autocomplete, Tooltip } from '@mui/material';
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import { ExpandMore, ExpandLess, AddToPhotos } from '@mui/icons-material';
import { LocalizationProvider, DesktopDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ImgUploadDialog from '../../../../components/ImgUpload/imgUpload';
import ServiceRelationManager from './ServiceRelationManager';
import { ItemDataContext } from './AddEditSectionItem';

const actionTypes = [
    { value: 'navigate', label: 'App Navigation' },
    { value: 'open_url', label: 'App URL' },
    { value: 'open_service', label: 'Tag Service' },
    { value: 'none', label: 'None' }
];

const mediaTypes = [
    { value: 'image', label: 'Image' },
    { value: 'gif', label: 'GIF' }
];

const positionOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' }
];

const badgePositionOptions = [
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-right', label: 'Bottom Right' }
];

const fontFamilyOptions = [
    { value: 'Default', label: 'Default' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Raleway', label: 'Raleway' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Playfair Display', label: 'Playfair Display' },
];
const fontWeightOptions = [
    { value: 300, label: 'Light (300)' },
    { value: 400, label: 'Regular (400)' },
    { value: 500, label: 'Medium (500)' },
    { value: 600, label: 'Semi Bold (600)' },
    { value: 700, label: 'Bold (700)' },
    { value: 800, label: 'Extra Bold (800)' },
];
const TAG_SERVICE_ROUTE = '/tagServicesScreen';

const formatValue = (value) => {
    if (!value) return '';  // Return empty string instead of 0
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '' : numValue.toString();
};

const styleLabelSx = { fontSize: '0.78rem', fontWeight: 600, mb: 0.5 };
const colorInputRowSx = {
    display: 'grid',
    gridTemplateColumns: '56px minmax(0, 1fr)',
    gap: 1,
    alignItems: 'center'
};
const colorPickerSx = {
    width: 56,
    '& input': { p: '6px' }
};

const ItemForm = ({ sectionData,setOpenGenerateAppUrl }) => {
    const {sectionList} = useContext(ItemDataContext)
    const { control, setValue, formState: { errors }, watch } = useFormContext();
    const [displayOptionsExpanded, setDisplayOptionsExpanded] = useState(false);
    const [textStylingExpanded, setTextStylingExpanded] = useState(true);
    const [cardDimensionsExpanded, setCardDimensionsExpanded] = useState(true);
    const [openImageUpload, setOpenImageUpload] = useState(false);
    const [currentImageField, setCurrentImageField] = useState(null);

    const actionType = useWatch({ control, name: 'actionType' });
    const mediaType = useWatch({ control, name: 'displayOptions.mediaType' }) || 'image';
    const showTitle = useWatch({ control, name: 'displayOptions.showTitle' });
    const showSubTitle = useWatch({ control, name: 'displayOptions.showSubTitle' });
    const showDescription = useWatch({ control, name: 'displayOptions.showDescription' });
    // if there there is error for description field, expand the display options by default, when displayDescription is set to false then required error will be removed and display options will be collapsed
    useEffect(() => {
        if (errors?.description && !showDescription) {
            //deregister error for description field
            setValue('description', '', { shouldDirty: true, shouldValidate: true });
            // setDisplayOptionsExpanded(false);
        }
    }, [showDescription]);

    const handleFileSelect = (fieldName) => {
        setCurrentImageField(fieldName);
        setOpenImageUpload(true);
    };

    const imgUploadResponse = (response) => {
        let imageUrl = response.imageBaseUrl + response.imageDirectory + response.imageName;
        setValue(currentImageField, imageUrl, { shouldDirty: true, shouldValidate: true });
        setOpenImageUpload(false);
        setCurrentImageField(null);
    };

    const renderActionDataFields = () => {
        switch (actionType) {
            case 'navigate':
                return (
                    <Grid item xs={12}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={11}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>App Navigation URL</Typography>
                                <Controller
                                    name="actionData.route"
                                    control={control}
                                    rules={{ required: 'App navigation URL is required' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            placeholder="/childServiceScreen?masterServiceTitle=..."
                                            error={!!errors.actionData?.route}
                                            helperText={errors.actionData?.route?.message || 'Generate or enter the in-app navigation/deep-link path'}
                                            InputProps={{ style: { fontSize: '0.75rem', whiteSpace: 'normal', wordBreak: 'break-all' } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <Tooltip title="Generate App Url">
                                    <IconButton size='small' color='primary' sx={{}} onClick={()=>setOpenGenerateAppUrl(true)}><AddToPhotos sx={{fontSize:'1rem',fontWeight:'bold'}}/></IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                );
            case 'open_url':
                return (
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>App URL</Typography>
                        <Controller
                            name="actionData.url"
                            control={control}
                            rules={{ required: 'App URL is required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    placeholder="https://example.com or /childServiceScreen?..."
                                    error={!!errors.actionData?.url}
                                    helperText={errors.actionData?.url?.message || 'Enter a web URL or app deep-link URL'}
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            )}
                        />
                    </Grid>
                );
            case 'open_service':
            default:
                return null;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12} md={8}>
                <Card sx={{ p: 2, pb: 4 }}>
                    <Grid container spacing={2} sx={{ pb: 2 }}>
                        <Grid item xs={12} md={7}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                Section<Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <Controller
                                name="sectionId"
                                control={control}
                                rules={{ required: 'Section type is required' }}
                                render={({ field, fieldState: { error } }) => (
                                    <Autocomplete
                                        fullWidth
                                        {...field}
                                        options={sectionList}
                                        getOptionLabel={(option) => option?.title || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === (value?.id || value)
                                        }
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue ? newValue.id : null);
                                        }}
                                        value={sectionList.find(option => option.id === field.value) || null}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                // label="Select SAC Code*"
                                                placeholder='Search and Select Section'
                                                error={!!error}
                                                // helperText={error ? error.message : ''}
                                                size="small"
                                                inputProps={{
                                                    ...params.inputProps,
                                                    style: { fontSize: '0.75rem' }
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <MenuItem {...props} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                                                {option.title || ''}
                                            </MenuItem>
                                        )}
                                    />
                                    
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Title<Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name="title"
                                control={control}
                                rules={{
                                    required: 'Enter item Title',
                                    maxLength: { value: 30, message: 'Max 30 characters' }
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.title}
                                        helperText={errors.title?.message || `${field.value?.length || 0}/30`}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ maxLength: 30 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle<Box component="span" sx={{ color: '#d32f2f' }}>*</Box></Typography>
                            <Controller
                                name="subtitle"
                                control={control}
                                rules={{
                                    required: 'Enter item Subtitle',
                                    maxLength: { value: 40, message: 'Max 40 characters' }
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.subtitle}
                                        helperText={errors.subtitle?.message || `${field.value?.length || 0}/40`}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{ maxLength: 40 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description{showDescription && <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>}</Typography>
                            <Controller
                                name="description"
                                control={control}
                                rules={{
                                    required: showDescription ? 'Enter item Description' : false
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        multiline
                                        rows={2}
                                        error={!!errors.description}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    py: 1,
                                    borderBottom: '1px solid #eee',
                                    '&:hover': { backgroundColor: '#fafafa' }
                                }}
                                onClick={() => setDisplayOptionsExpanded(!displayOptionsExpanded)}
                            >
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Display Options</Typography>
                                <IconButton size="small">
                                    {displayOptionsExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            <Collapse in={displayOptionsExpanded}>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Controller
                                                name="displayOptions.showTitle"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} size="small" />}
                                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Show Title</Typography>}
                                                    />
                                                )}
                                            />
                                            {showTitle && (
                                                <Controller
                                                    name="displayOptions.titlePosition"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            InputProps={{ style: { fontSize: '0.7rem' } }}
                                                        >
                                                            {positionOptions.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Controller
                                                name="displayOptions.showSubTitle"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} size="small" />}
                                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Show Subtitle</Typography>}
                                                    />
                                                )}
                                            />
                                            {showSubTitle && (
                                                <Controller
                                                    name="displayOptions.subTitlePosition"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            InputProps={{ style: { fontSize: '0.7rem' } }}
                                                        >
                                                            {positionOptions.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Controller
                                                name="displayOptions.showDescription"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControlLabel
                                                        control={<Switch {...field} checked={field.value} size="small" />}
                                                        label={<Typography sx={{ fontSize: '0.75rem' }}>Show Description</Typography>}
                                                    />
                                                )}
                                            />
                                            {showDescription && (
                                                <Controller
                                                    name="displayOptions.descriptionPosition"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            select
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            InputProps={{ style: { fontSize: '0.7rem' } }}
                                                        >
                                                            {positionOptions.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>

                        {/* Text Styling Section */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    py: 1,
                                    borderBottom: '1px solid #eee',
                                    '&:hover': { backgroundColor: '#fafafa' }
                                }}
                                onClick={() => setTextStylingExpanded(!textStylingExpanded)}
                            >
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Text Styling</Typography>
                                <IconButton size="small">
                                    {textStylingExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            <Collapse in={textStylingExpanded}>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    {/* Title Styling */}

                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Title Color</Typography>
                                        <Box sx={colorInputRowSx}>
                                            <Controller
                                                name="displayOptions.titleColor"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        type="color"
                                                        size="small"
                                                        sx={colorPickerSx}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="displayOptions.titleColor"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        size="small"
                                                        placeholder="#000000"
                                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Title Font Family</Typography>
                                        <Controller
                                            name="displayOptions.titleFontFamily"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                                >
                                                    {fontFamilyOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Title Font Size (px)</Typography>
                                        <Controller
                                            name="displayOptions.titleFontSize"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    fullWidth
                                                    size="small"
                                                    placeholder="14"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, inputProps: { min: 8, max: 72 } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Subtitle Color</Typography>
                                        <Box sx={colorInputRowSx}>
                                            <Controller
                                                name="displayOptions.subTitleColor"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        type="color"
                                                        size="small"
                                                        sx={colorPickerSx}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="displayOptions.subTitleColor"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        size="small"
                                                        placeholder="#555555"
                                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Subtitle Font Family</Typography>
                                        <Controller
                                            name="displayOptions.subTitleFontFamily"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                                >
                                                    {fontFamilyOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Subtitle Font Size (px)</Typography>
                                        <Controller
                                            name="displayOptions.subTitleFontSize"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    fullWidth
                                                    size="small"
                                                    placeholder="12"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, inputProps: { min: 8, max: 72 } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Description Color</Typography>
                                        <Box sx={colorInputRowSx}>
                                            <Controller
                                                name="displayOptions.descriptionColor"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        type="color"
                                                        size="small"
                                                        sx={colorPickerSx}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="displayOptions.descriptionColor"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        size="small"
                                                        placeholder="#777777"
                                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Description Font Family</Typography>
                                        <Controller
                                            name="displayOptions.descriptionFontFamily"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                                >
                                                    {fontFamilyOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography sx={styleLabelSx}>Description Font Size (px)</Typography>
                                        <Controller
                                            name="displayOptions.descriptionFontSize"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    fullWidth
                                                    size="small"
                                                    placeholder="12"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, inputProps: { min: 8, max: 72 } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    {[
                                        { key: 'title', label: 'Title' },
                                        { key: 'subTitle', label: 'Subtitle' },
                                        { key: 'description', label: 'Description' },
                                    ].map(textStyle => (
                                        <Grid item xs={12} sm={4} key={textStyle.key}>
                                            <Typography sx={styleLabelSx}>{textStyle.label} Font Weight</Typography>
                                            <Controller
                                                name={`displayOptions.${textStyle.key}FontWeight`}
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        select
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                                    >
                                                        {fontWeightOptions.map(option => (
                                                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Collapse>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                Media Type<Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <Controller
                                name="displayOptions.mediaType"
                                control={control}
                                rules={{ required: 'Media type is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        onChange={(event) => {
                                            const hasMediaTypeChanged = event.target.value !== field.value;
                                            field.onChange(event);
                                            if (hasMediaTypeChanged) {
                                                setValue('imageUrl', null, { shouldDirty: true, shouldValidate: true });
                                            }
                                        }}
                                        select
                                        fullWidth
                                        size="small"
                                        error={!!errors.displayOptions?.mediaType}
                                        helperText={errors.displayOptions?.mediaType?.message}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {mediaTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.75rem' }}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                {mediaType === 'gif' ? 'GIF' : 'Image'}
                                <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Controller
                                    name="imageUrl"
                                    control={control}
                                    rules={{
                                        validate: value => {
                                            const selectedMediaType = watch('displayOptions.mediaType') || 'image';
                                            const mediaLabel = selectedMediaType === 'gif' ? 'GIF' : 'Image';
                                            return !!value || `${mediaLabel} is required`;
                                        }
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            error={!!errors.imageUrl}
                                            helperText={errors.imageUrl?.message}
                                            InputProps={{ style: { fontSize: '0.75rem' }, readOnly: true }}
                                            placeholder={`No ${mediaType === 'gif' ? 'GIF' : mediaType} selected`}
                                        />
                                    )}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.7rem',
                                        height: 40,
                                        minWidth: 80
                                    }}
                                    onClick={() => handleFileSelect('imageUrl')}
                                >
                                    Upload
                                </Button>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Badge Text</Typography>
                            <Controller
                                name="badgeText"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Badge Color</Typography>
                            <Box sx={colorInputRowSx}>
                                <Controller
                                    name="badgeColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="color"
                                            size="small"
                                            sx={colorPickerSx}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="badgeColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            placeholder="#FF6B35"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Badge Text Color</Typography>
                            <Box sx={colorInputRowSx}>
                                <Controller
                                    name="badgeTextColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="color"
                                            size="small"
                                            sx={colorPickerSx}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="badgeTextColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            placeholder="#FFFFFF"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Badge Position</Typography>
                            <Controller
                                name="badgePosition"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {badgePositionOptions.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {/* <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Position</Typography>
                            <Controller
                                name="position"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        disabled
                                        type="number"
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                        inputProps={{
                                            style: { textAlign: 'center', height: '1rem', fontSize: '0.75rem' },
                                            onFocus: (e) => e.target.select(), // This will auto-select all text on focus/click
                                            onClick: (e) => e.target.select(),  // This ensures selection even on click when already focused
                                            onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
                                            onKeyDown: (e) => {
                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                }
                                            }
                                        }}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            let finalValue;
                                            if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
                                            
                                                finalValue = formatValue(inputValue);
                                                console.log("finalValue",finalValue)

                                                if(finalValue==''){
                                                    // finalValue=0
                                                }
                                                else {
                                                    const numValue = parseFloat(finalValue);
                                                    if (!isNaN(numValue) && numValue > 1000) {
                                                        finalValue = '1000';
                                                    }
                                                }

                                                field.onChange(finalValue);
                                            }
                                        }}   
                                    />
                                )}
                            />
                        </Grid> */}

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Card Background Color</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Controller
                                    name="cardBackgroundColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="color"
                                            size="small"
                                            sx={{ width: 60 }}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="cardBackgroundColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            placeholder="#FFFFFF"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Card Border Color</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Controller
                                    name="cardBorderColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="color"
                                            size="small"
                                            sx={{ width: 60 }}
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                                <Controller
                                    name="cardBorderColor"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            placeholder="#E0E0E0"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>

                        {/* Card Dimensions Section */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    py: 1,
                                    borderBottom: '1px solid #eee',
                                    '&:hover': { backgroundColor: '#fafafa' }
                                }}
                                onClick={() => setCardDimensionsExpanded(!cardDimensionsExpanded)}
                            >
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Card Dimensions</Typography>
                                <IconButton size="small">
                                    {cardDimensionsExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                            <Collapse in={cardDimensionsExpanded}>
                                <Grid container spacing={1} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Card Width (px)</Typography>
                                        <Controller
                                            name="displayOptions.cardWidth"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    fullWidth
                                                    size="small"
                                                    placeholder="e.g. 160"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, inputProps: { min: 0 } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Card Height (px)</Typography>
                                        <Controller
                                            name="displayOptions.cardHeight"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    type="number"
                                                    fullWidth
                                                    size="small"
                                                    placeholder="e.g. 200"
                                                    InputProps={{ style: { fontSize: '0.75rem' }, inputProps: { min: 0 } }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Margin */}
                                    <Grid item xs={12}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', mb: 0.5 }}>Margin (px)</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Top</Typography>
                                        <Controller
                                            name="displayOptions.marginTop"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Right</Typography>
                                        <Controller
                                            name="displayOptions.marginRight"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Bottom</Typography>
                                        <Controller
                                            name="displayOptions.marginBottom"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Left</Typography>
                                        <Controller
                                            name="displayOptions.marginLeft"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>

                                    {/* Padding */}
                                    <Grid item xs={12}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', mb: 0.5, mt: 0.5 }}>Padding (px)</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Top</Typography>
                                        <Controller
                                            name="displayOptions.paddingTop"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Right</Typography>
                                        <Controller
                                            name="displayOptions.paddingRight"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Bottom</Typography>
                                        <Controller
                                            name="displayOptions.paddingBottom"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Left</Typography>
                                        <Controller
                                            name="displayOptions.paddingLeft"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField {...field} type="number" fullWidth size="small" placeholder="0"
                                                    InputProps={{ style: { fontSize: '0.75rem' } }} />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>

                        {/* <Grid item xs={12} sm={6}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch {...field} checked={field.value} size="small" />}
                                        label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Active</Typography>}
                                    />
                                )}
                            />
                        </Grid> */}

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Date/Time</Typography>
                            <Controller
                                name="startDateTime"
                                control={control}
                                render={({ field }) => (
                                    <DesktopDateTimePicker
                                        {...field}
                                        format="DD/MM/YYYY hh:mm A"
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(newValue) => field.onChange(newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                inputProps: { style: { fontSize: '0.75rem' } }
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>End Date/Time</Typography>
                                <Button
                                    size="small"
                                    variant="text"
                                    sx={{ minWidth: 'auto', p: 0, fontSize: '0.7rem', textTransform: 'none' }}
                                    onClick={() => setValue('endDateTime', null, { shouldDirty: true, shouldValidate: true })}
                                >
                                    Clear
                                </Button>
                            </Box>
                            <Controller
                                name="endDateTime"
                                control={control}
                                render={({ field }) => (
                                    <DesktopDateTimePicker
                                        {...field}
                                        format="DD/MM/YYYY hh:mm A"
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(newValue) => field.onChange(newValue ? newValue.format('YYYY-MM-DDTHH:mm') : null)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                inputProps: { style: { fontSize: '0.75rem' } }
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                Action Type<Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                            </Typography>
                            <Controller
                                name="actionType"
                                control={control}
                                rules={{ required: 'Action type is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        onChange={(event) => {
                                            const nextActionType = event.target.value;
                                            field.onChange(event);

                                            if (nextActionType === 'open_service') {
                                                setValue('actionData.route', TAG_SERVICE_ROUTE, {
                                                    shouldDirty: true
                                                });
                                            } else if (
                                                nextActionType === 'navigate' &&
                                                watch('actionData.route') === TAG_SERVICE_ROUTE
                                            ) {
                                                setValue('actionData.route', '', {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                            }
                                        }}
                                        select
                                        fullWidth
                                        size="small"
                                        error={!!errors.actionType}
                                        helperText={errors.actionType?.message}
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    >
                                        {actionTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.75rem' }}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {renderActionDataFields()}
                    </Grid>
                </Card>

                {actionType === 'open_service' && <Box sx={{ mt: 2, mb: 4 }}>
                    <ServiceRelationManager />
                </Box>}
            </Grid>

            <ImgUploadDialog
                open={openImageUpload}
                onClose={() => {
                    setOpenImageUpload(false);
                    setCurrentImageField(null);
                }}
                imgData={{ path: 'customer-app/items' }}
                imgUploadResponse={imgUploadResponse}
                allowedTypes={
                    mediaType === 'gif'
                        ? ['image/gif']
                        : ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
                }
                allowedTypesErrorMsg={
                    mediaType === 'gif'
                        ? 'File type not supported. Please upload a GIF image.'
                        : 'File type not supported. Please upload a PNG, JPEG, JPG, or WebP image.'
                }
                maxFileSize={
                    mediaType === 'gif'
                        ? 10 * 1024 * 1024
                        : 2 * 1024 * 1024
                }
                keepOriginal={mediaType === 'gif'}
            />
        </LocalizationProvider>
    );
};

export default ItemForm;
